/**
 * ReferralCodeManager - 推荐码管理器
 * 
 * 功能：
 * 1. 生成随机推荐码
 * 2. 验证推荐码唯一性
 * 3. 推荐码 ↔ UID 映射
 * 4. 自定义推荐码管理
 */

import { logger } from '../utils/logger';
import { storage } from '../utils/storage';

const STORAGE_KEY = 'referral_code_index';
const CHANGE_TIME_KEY = 'referral_code_change_time';
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

// 排除易混淆字符：0,O,1,I
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export class ReferralCodeManager {
  private codeToUidMap: Map<string, string> = new Map();

  constructor() {
    this.load();
    logger.log('ReferralCodeManager initialized');
  }

  /**
   * 生成随机推荐码（6位大写字母+数字）
   */
  generateReferralCode(): string {
    let code = '';
    let attempts = 0;
    const maxAttempts = 100;

    do {
      code = '';
      for (let i = 0; i < 6; i++) {
        code += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
      }
      attempts++;

      if (attempts >= maxAttempts) {
        logger.error('Failed to generate unique referral code after 100 attempts');
        throw new Error('无法生成唯一推荐码');
      }
    } while (this.isCodeExists(code));

    logger.log(`Generated referral code: ${code}`);
    return code;
  }

  /**
   * 验证推荐码是否存在
   */
  isCodeExists(code: string): boolean {
    return this.codeToUidMap.has(code.toUpperCase());
  }

  /**
   * 注册推荐码
   */
  registerCode(code: string, uid: string): boolean {
    const upperCode = code.toUpperCase();

    if (this.isCodeExists(upperCode)) {
      logger.warn(`Referral code already exists: ${upperCode}`);
      return false;
    }

    this.codeToUidMap.set(upperCode, uid);
    this.save();
    logger.log(`Registered referral code: ${upperCode} → ${uid}`);
    return true;
  }

  /**
   * 通过推荐码获取UID
   */
  getUidByCode(code: string): string | null {
    return this.codeToUidMap.get(code.toUpperCase()) || null;
  }

  /**
   * 获取用户的所有推荐码
   */
  getAllCodesForUser(uid: string): string[] {
    const codes: string[] = [];

    this.codeToUidMap.forEach((mappedUid, code) => {
      if (mappedUid === uid) {
        codes.push(code);
      }
    });

    return codes;
  }

  /**
   * 验证自定义推荐码
   */
  validateCustomCode(code: string): { valid: boolean; error?: string } {
    // 长度检查
    if (code.length < 4 || code.length > 12) {
      return { valid: false, error: '推荐码长度必须在4-12位之间' };
    }

    // 格式检查
    if (!/^[a-zA-Z0-9_]+$/.test(code)) {
      return { valid: false, error: '推荐码只能包含字母、数字和下划线' };
    }

    // 唯一性检查
    if (this.isCodeExists(code)) {
      return { valid: false, error: '该推荐码已被使用' };
    }

    // 敏感词检查
    const bannedWords = ['admin', 'system', 'root', 'test', 'fuck', 'shit', 'bitch', 'damn'];
    const lowerCode = code.toLowerCase();
    if (bannedWords.some(word => lowerCode.includes(word))) {
      return { valid: false, error: '推荐码包含敏感词' };
    }

    return { valid: true };
  }

  /**
   * 设置自定义推荐码
   */
  setCustomCode(uid: string, customCode: string): { success: boolean; error?: string } {
    // 验证推荐码
    const validation = this.validateCustomCode(customCode);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // 检查修改次数限制（每月1次）
    const lastChangeTime = this.getLastCodeChangeTime(uid);
    const now = Date.now();

    if (lastChangeTime && (now - lastChangeTime) < ONE_MONTH_MS) {
      const remainingDays = Math.ceil((ONE_MONTH_MS - (now - lastChangeTime)) / (24 * 60 * 60 * 1000));
      return {
        success: false,
        error: `每月只能修改一次推荐码，请${remainingDays}天后再试`
      };
    }

    // 注册新推荐码（不删除旧推荐码）
    if (!this.registerCode(customCode, uid)) {
      return { success: false, error: '注册推荐码失败' };
    }

    // 记录修改时间
    this.setLastCodeChangeTime(uid, now);

    logger.log(`Custom referral code set: ${uid} → ${customCode}`);
    return { success: true };
  }

  /**
   * 获取上次修改时间
   */
  private getLastCodeChangeTime(uid: string): number | null {
    const data = storage.get<Record<string, number>>(CHANGE_TIME_KEY, {});
    return data[uid] || null;
  }

  /**
   * 设置上次修改时间
   */
  private setLastCodeChangeTime(uid: string, time: number): void {
    const data = storage.get<Record<string, number>>(CHANGE_TIME_KEY, {});
    data[uid] = time;
    storage.set(CHANGE_TIME_KEY, data);
  }

  /**
   * 检查是否可以修改推荐码
   */
  canChangeCode(uid: string): boolean {
    const lastChangeTime = this.getLastCodeChangeTime(uid);
    if (!lastChangeTime) return true;

    const now = Date.now();
    return (now - lastChangeTime) >= ONE_MONTH_MS;
  }

  /**
   * 获取剩余可修改天数
   */
  getRemainingDays(uid: string): number {
    const lastChangeTime = this.getLastCodeChangeTime(uid);
    if (!lastChangeTime) return 0;

    const now = Date.now();
    const elapsed = now - lastChangeTime;
    if (elapsed >= ONE_MONTH_MS) return 0;

    return Math.ceil((ONE_MONTH_MS - elapsed) / (24 * 60 * 60 * 1000));
  }

  /**
   * 保存到 localStorage
   */
  private save(): void {
    const data: Record<string, string> = {};
    this.codeToUidMap.forEach((uid, code) => {
      data[code] = uid;
    });
    storage.set(STORAGE_KEY, data);
  }

  /**
   * 从 localStorage 加载
   */
  private load(): void {
    const data = storage.get<Record<string, string>>(STORAGE_KEY, {});
    Object.entries(data).forEach(([code, uid]) => {
      this.codeToUidMap.set(code, uid);
    });
    logger.log(`Loaded ${this.codeToUidMap.size} referral codes`);
  }

  /**
   * 清空所有推荐码（仅用于测试）
   */
  clearAll(): void {
    this.codeToUidMap.clear();
    storage.remove(STORAGE_KEY);
    storage.remove(CHANGE_TIME_KEY);
    logger.log('All referral codes cleared');
  }
}

